import React, { useEffect, useState } from 'react';
import {
    FaChartLine, FaLightbulb, FaTimeline, FaCircleExclamation, FaTriangleExclamation, FaRegClock,
    FaBullseye, FaChartPie, FaHandshake, FaCircleCheck, FaListCheck, FaFaceFrown, FaDollarSign,
    FaPhone, FaPhoneSlash, FaBell, FaEnvelope, FaUserGroup, FaHouseChimneyCrack, FaVoicemail, FaRobot,
    FaBan, FaPersonWalking, FaChevronDown,
} from 'react-icons/fa6';
import axios from '../../../configApi/axiosConfig';
import { APIURL } from '../../../configApi/apiConfig';

// ─── Formatos ──────────────────────────────────────────────────────────────────
// Las fechas de pago llegan como texto 'YYYY-MM-DD' (sin hora) para no correrlas de día.
const fmtDia = (s) => {
    if (!s) return '-';
    const [y, m, d] = String(s).slice(0, 10).split('-');
    return y && m && d ? `${d}/${m}/${y}` : '-';
};
const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (f) => new Date(f).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
const hoyIso = () => {
    const h = new Date();
    return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}`;
};
const diasDesde = (fecha) => (fecha ? Math.floor((Date.now() - new Date(fecha).getTime()) / 86400000) : null);
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const hora = (h) => `${String(h).padStart(2, '0')}:00`;

// ─── Puntos clave por reglas (sin IA): cada uno sale de un dato de la base ─────
// Paleta del mockup: claro / oscuro (bg-*-950/40, texto *-300)
const NIVEL = {
    critico: { caja: 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-950/40', icono: 'text-rose-500 dark:text-rose-400', titulo: 'text-rose-700 dark:text-rose-300', texto: 'text-rose-700/90 dark:text-rose-200/90' },
    aviso: { caja: 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/40', icono: 'text-amber-500 dark:text-amber-400', titulo: 'text-amber-800 dark:text-amber-300', texto: 'text-amber-800/90 dark:text-amber-200/90' },
    bueno: { caja: 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-950/40', icono: 'text-emerald-500 dark:text-emerald-400', titulo: 'text-emerald-700 dark:text-emerald-300', texto: 'text-emerald-700/90 dark:text-emerald-200/90' },
    neutro: { caja: 'border-slate-200 bg-slate-50 dark:border-slate-700/60 dark:bg-[#161F30]', icono: 'text-slate-500 dark:text-slate-400', titulo: 'text-slate-800 dark:text-slate-200', texto: 'text-slate-600 dark:text-slate-400' },
    azul: { caja: 'border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/40', icono: 'text-blue-500 dark:text-blue-400', titulo: 'text-blue-800 dark:text-blue-300', texto: 'text-blue-800/90 dark:text-blue-200/90' },
    indigo: { caja: 'border-indigo-200 bg-indigo-50 dark:border-indigo-800/50 dark:bg-indigo-950/40', icono: 'text-indigo-500 dark:text-indigo-400', titulo: 'text-indigo-800 dark:text-indigo-300', texto: 'text-indigo-800/90 dark:text-indigo-200/90' },
};

export function generarInsights(e) {
    const lista = [];
    const hoy = hoyIso();

    if (e.UltimaPromesaFechaPago) {
        const pagado = Number(e.PagadoDesdeUltimaPromesa || 0);
        if (e.UltimaPromesaFechaPago >= hoy) {
            lista.push({ nivel: 'azul', Icono: FaHandshake, titulo: 'Promesa vigente:', texto: `${fmtUsd(e.UltimaPromesaValor)} para el ${fmtDia(e.UltimaPromesaFechaPago)}.` });
        } else if (pagado > 0) {
            lista.push({ nivel: 'bueno', Icono: FaCircleCheck, titulo: 'Pago tras promesa:', texto: `pagó ${fmtUsd(pagado)} después de prometer ${fmtUsd(e.UltimaPromesaValor)} para el ${fmtDia(e.UltimaPromesaFechaPago)}.` });
        } else {
            lista.push({ nivel: 'critico', Icono: FaCircleExclamation, titulo: 'Promesa vencida:', texto: `${fmtUsd(e.UltimaPromesaValor)} para el ${fmtDia(e.UltimaPromesaFechaPago)}, sin pago registrado.` });
        }
    }

    const g30 = e.Gestiones30d || 0;
    const directos = e.ContactosDirectos30d || 0;
    if (g30 >= 15) {
        lista.push({ nivel: 'aviso', Icono: FaTriangleExclamation, titulo: 'Exceso de contacto:', texto: `gestionado ${g30} veces en 30 días, ${plural(directos, 'contacto directo', 'contactos directos')}.` });
    } else if (g30 > 0 && directos === 0) {
        lista.push({ nivel: 'aviso', Icono: FaTriangleExclamation, titulo: 'Baja contactabilidad:', texto: `gestionado ${plural(g30, 'vez', 'veces')} en 30 días, con 0 contacto directo.` });
    } else {
        lista.push({ nivel: 'neutro', Icono: FaListCheck, titulo: 'Actividad:', texto: `gestionado ${plural(g30, 'vez', 'veces')} en 30 días, ${plural(directos, 'contacto directo', 'contactos directos')}.` });
    }

    const dias = diasDesde(e.UltimoContactoDirecto);
    if (dias === null) {
        lista.push({ nivel: 'aviso', Icono: FaRegClock, titulo: 'Sin contacto directo:', texto: 'nunca se ha logrado hablar con el titular.' });
    } else if (dias > 30) {
        lista.push({ nivel: 'neutro', Icono: FaRegClock, titulo: 'Sin contacto directo:', texto: `han pasado ${dias} días desde el último.` });
    }

    if (e.HoraFrecuenteContacto !== null && e.HoraFrecuenteContacto !== undefined) {
        const h = Number(e.HoraFrecuenteContacto);
        lista.push({
            nivel: 'azul', Icono: FaBullseye, titulo: 'Mejor hora:',
            texto: `más contactos directos entre las ${hora(h)} y ${hora(h + 1)} (${plural(e.HoraFrecuenteContactos, 'contacto', 'contactos')} en 6 meses).`,
        });
    }

    if (e.CanalMasEfectivo) {
        lista.push({ nivel: 'indigo', Icono: FaChartPie, titulo: 'Canal más efectivo:', texto: `${e.CanalMasEfectivo} (${e.CanalMasEfectivoTasa} % de sus gestiones con contacto directo).` });
    }

    if ((e.Analizadas || 0) > 0 && (e.Negativas || 0) > 0) {
        lista.push({ nivel: 'aviso', Icono: FaFaceFrown, titulo: 'Ánimo del cliente:', texto: `negativo en ${e.Negativas} de sus últimas ${e.Analizadas} llamadas analizadas${e.UltimaActitud ? ` (última: ${e.UltimaActitud})` : ''}.` });
    }

    return lista;
}

// ─── Tarjetas KPI ──────────────────────────────────────────────────────────────
const TONO = {
    normal: { caja: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-[#161F30]', etiqueta: 'text-slate-500 dark:text-slate-400', valor: 'text-slate-900 dark:text-white', detalle: 'text-slate-500 dark:text-slate-400' },
    rojo: { caja: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-[#161F30]', etiqueta: 'text-slate-500 dark:text-slate-400', valor: 'text-rose-600 dark:text-rose-400', detalle: 'text-slate-500 dark:text-slate-400' },
    alerta: { caja: 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-950/30', etiqueta: 'text-rose-600 dark:text-rose-400', valor: 'text-rose-600 dark:text-rose-400', detalle: 'text-rose-600 dark:text-rose-300 font-medium' },
    verde: { caja: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-[#161F30]', etiqueta: 'text-slate-500 dark:text-slate-400', valor: 'text-emerald-600 dark:text-emerald-400', detalle: 'text-slate-500 dark:text-slate-400' },
    indigo: { caja: 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-[#161F30]', etiqueta: 'text-slate-500 dark:text-slate-400', valor: 'text-indigo-600 dark:text-indigo-300', detalle: 'text-indigo-600 dark:text-indigo-400 font-medium' },
};

function Tarjeta({ etiqueta, valor, detalle, tono = 'normal', pequeno = false }) {
    const t = TONO[tono];
    return (
        <div className={`p-3 rounded-lg border flex flex-col justify-between ${t.caja}`}>
            <span className={`text-[11px] font-semibold uppercase ${t.etiqueta}`}>{etiqueta}</span>
            <div className="my-1">
                <span className={`${pequeno ? 'text-base truncate block' : 'text-2xl'} font-black ${t.valor}`}>{valor}</span>
            </div>
            <span className={`text-[11px] ${t.detalle}`}>{detalle || ' '}</span>
        </div>
    );
}

// ─── Línea de tiempo ───────────────────────────────────────────────────────────
const COLOR = {
    pago: 'bg-emerald-600', promesa: 'bg-amber-500', directo: 'bg-blue-600',
    indirecto: 'bg-purple-600', sinContacto: 'bg-slate-500', negativo: 'bg-rose-600',
};
const COLOR_TEXTO = {
    pago: 'text-emerald-600 dark:text-emerald-300', promesa: 'text-amber-600 dark:text-amber-400', directo: 'text-blue-600 dark:text-blue-300',
    indirecto: 'text-purple-600 dark:text-purple-300', sinContacto: 'text-slate-500 dark:text-slate-400', negativo: 'text-rose-600 dark:text-rose-400',
};

/** Ícono, color y etiqueta de un evento (gestión o pago) de la línea de tiempo. */
const estiloEvento = (ev) => {
    if (ev.Evento === 'PAGO') return { tipo: 'pago', Icono: FaDollarSign, etiqueta: `Pago ${fmtUsd(ev.Valor)}` };
    const r = (ev.Resultado || '').toUpperCase();
    const ct = (ev.TipoContacto || '').toUpperCase();
    let estilo;
    if (ev.EsPromesa) estilo = { tipo: 'promesa', Icono: FaHandshake };
    else if (r.includes('NEGATIVA') || r.includes('EQUIVOCADO')) estilo = { tipo: 'negativo', Icono: FaBan };
    else if (r.includes('NO HAY NADIE')) estilo = { tipo: 'sinContacto', Icono: FaHouseChimneyCrack };
    else if (r.includes('GRABADORA')) estilo = { tipo: 'sinContacto', Icono: FaVoicemail };
    else if (ct.startsWith('NO CONTACTO')) estilo = { tipo: 'sinContacto', Icono: FaPhoneSlash };
    else if (ct.includes('INDIRECTO')) {
        estilo = { tipo: 'indirecto', Icono: r.includes('NOTIF') ? FaBell : r.includes('MENSAJE') ? FaEnvelope : FaUserGroup };
    } else estilo = { tipo: 'directo', Icono: ev.Canal === 'COBRADOR' ? FaPersonWalking : FaPhone };
    const auto = ev.Origen === 'AUTO_CDR';
    return { ...estilo, Icono: auto ? FaRobot : estilo.Icono, etiqueta: `${auto ? 'Bot: ' : ''}${ev.Resultado || ''}` };
};

const LEYENDA = [
    ['pago', 'Pago'], ['promesa', 'Promesa'], ['directo', 'Contacto directo'],
    ['indirecto', 'Contacto indirecto / notif.'], ['sinContacto', 'Sin contacto / no contesta'], ['negativo', 'Negativa / equivocado'],
];

function LineaTiempo({ eventos }) {
    const [abierto, setAbierto] = useState(false);
    // La más antigua a la izquierda, como una línea de tiempo
    const ordenados = [...(eventos || [])].reverse();

    return (
        <div className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#0D131F] transition-colors">
            {/* Cabecera / Barra para mostrar u ocultar la línea de tiempo */}
            <div className="px-4 py-2.5 flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={() => setAbierto((prev) => !prev)}
                    className="flex items-center gap-2 text-left group cursor-pointer focus:outline-none"
                    title={abierto ? 'Ocultar línea de tiempo' : 'Mostrar línea de tiempo'}
                >
                    <FaTimeline className="text-xs text-indigo-500 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
                    <h3 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        Línea de tiempo del crédito
                    </h3>
                    {ordenados.length > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            {ordenados.length} {ordenados.length === 1 ? 'evento' : 'eventos'}
                        </span>
                    )}
                </button>

                <div className="flex items-center gap-3">
                    {/* Leyenda visible sólo cuando está abierto en pantallas medianas/grandes */}
                    {abierto && (
                        <div className="hidden lg:flex items-center gap-3 text-[11px] text-slate-600 dark:text-slate-300 animate-fadeIn">
                            {LEYENDA.map(([tipo, texto]) => (
                                <span key={tipo} className="flex items-center gap-1.5">
                                    <span className={`w-2.5 h-2.5 rounded-full ${COLOR[tipo]}`} /> {texto}
                                </span>
                            ))}
                            <span className="flex items-center gap-1.5"><FaRobot className="text-violet-500" /> Automática</span>
                        </div>
                    )}

                    {/* Botón interactivo para alternar visibilidad */}
                    <button
                        type="button"
                        onClick={() => setAbierto((prev) => !prev)}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200/80 dark:border-indigo-800/80 transition-all cursor-pointer shadow-xs hover:shadow-sm"
                    >
                        <span>{abierto ? 'Ocultar línea de tiempo' : 'Ver línea de tiempo'}</span>
                        <FaChevronDown className={`text-[10px] transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Contenido desplegable (oculto por defecto) */}
            {abierto && (
                <div className="px-4 pb-3 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 animate-fadeIn">
                    {/* Leyenda en móviles/tablets cuando está desplegado */}
                    <div className="flex lg:hidden flex-wrap items-center gap-2 mb-2 pb-2 border-b border-slate-200/60 dark:border-slate-800/60 text-[10px] text-slate-600 dark:text-slate-300">
                        {LEYENDA.map(([tipo, texto]) => (
                            <span key={tipo} className="flex items-center gap-1">
                                <span className={`w-2 h-2 rounded-full ${COLOR[tipo]}`} /> {texto}
                            </span>
                        ))}
                        <span className="flex items-center gap-1"><FaRobot className="text-violet-500" /> Auto</span>
                    </div>

                    {ordenados.length === 0 ? (
                        <p className="text-xs text-slate-400 py-2">Sin movimientos.</p>
                    ) : (
                        <div className="relative overflow-x-auto pb-2 pt-2">
                            <div className="relative flex items-start space-x-6 min-w-max px-2">
                                <div className="absolute top-[14px] left-4 right-4 h-0.5 bg-slate-200 dark:bg-slate-700 z-0" />
                                {ordenados.map((ev, i) => {
                                    const { tipo, Icono, etiqueta } = estiloEvento(ev);
                                    return (
                                        <div
                                            key={i}
                                            className="relative z-10 flex flex-col items-center text-center w-20"
                                            title={[ev.Resultado, ev.Canal, ev.Origen === 'AUTO_CDR' ? 'gestión automática' : null].filter(Boolean).join(' · ')}
                                        >
                                            <div className={`w-7 h-7 rounded-full ${COLOR[tipo]} text-white flex items-center justify-center text-[10px] ring-4 ring-slate-50 dark:ring-[#0D131F] shadow-sm`}>
                                                <Icono />
                                            </div>
                                            <span className={`text-[10px] font-bold mt-1.5 ${tipo === 'pago' ? COLOR_TEXTO.pago : 'text-slate-700 dark:text-slate-300'}`}>
                                                {fmtFecha(ev.Fecha)}
                                            </span>
                                            <span className={`text-[9px] font-semibold uppercase leading-tight mt-0.5 ${COLOR_TEXTO[tipo]}`}>
                                                {etiqueta}
                                            </span>
                                            {ev.EsPromesa === 1 && ev.FechaPago && (
                                                <span className="text-[9px] text-amber-600 dark:text-amber-400">
                                                    {fmtUsd(ev.Valor)} al {fmtDia(ev.FechaPago)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Panel ─────────────────────────────────────────────────────────────────────
/**
 * Estadísticas del crédito, puntos clave por reglas y línea de tiempo (gestiones + pagos).
 * `recargar` cambia cuando se recarga el historial, para volver a pedir los datos.
 */
export function PanelCredito({ idCompra, recargar }) {
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [actualizado, setActualizado] = useState(null);

    useEffect(() => {
        if (!idCompra) return undefined;
        let vigente = true;
        setCargando(true);
        axios.get(APIURL.getEstadisticasCredito(idCompra))
            .then((res) => {
                if (!vigente) return;
                setDatos(res.data || null);
                setActualizado(new Date());
            })
            .catch((error) => {
                console.error('Error obteniendo estadísticas del crédito:', error);
                if (vigente) setDatos(null);
            })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [idCompra, recargar]);

    if (!idCompra) return null;
    if (cargando && !datos) {
        return (
            <div className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 p-6 text-center text-slate-400 text-sm">
                Cargando estadísticas del crédito…
            </div>
        );
    }
    if (!datos) return null;

    const insights = generarInsights(datos);
    const tieneHora = datos.HoraFrecuenteContacto !== null && datos.HoraFrecuenteContacto !== undefined;

    return (
        <section className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            {/* Encabezado */}
            <div className="bg-slate-50 dark:bg-[#0D131F] border-b border-slate-200 dark:border-slate-800 px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                    <FaChartLine className="text-sm text-indigo-500 dark:text-indigo-400" />
                    <h2 className="text-xs font-bold tracking-wider uppercase">Estadísticas del crédito</h2>
                </div>
                {actualizado && (
                    <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-300">
                        <span className={`w-2 h-2 rounded-full bg-emerald-400 ${cargando ? 'animate-pulse' : ''}`} />
                        Actualizado {actualizado.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
            </div>

            <div className="p-4 grid grid-cols-1 xl:grid-cols-12 gap-4">
                {/* Puntos clave */}
                <div className="xl:col-span-4 space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200 mb-1">
                        <FaLightbulb className="text-amber-400" />
                        <span>Puntos clave y alertas</span>
                    </div>
                    {insights.map(({ nivel, Icono, titulo, texto }, i) => {
                        const n = NIVEL[nivel];
                        return (
                            <div key={i} className={`p-2.5 rounded-lg border text-xs flex items-start gap-2.5 ${n.caja}`}>
                                <Icono className={`mt-0.5 text-xs flex-shrink-0 ${n.icono}`} />
                                <div>
                                    <span className={`font-bold ${n.titulo}`}>{titulo}</span>
                                    <span className={n.texto}> {texto}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* KPI */}
                <div className="xl:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-3 content-start">
                    <Tarjeta etiqueta="Total gestiones" valor={datos.TotalGestiones ?? 0} detalle={`${datos.Gestiones30d ?? 0} en los últimos 30 días`} />
                    <Tarjeta etiqueta="Contactos directos" valor={datos.ContactosDirectos30d ?? 0} detalle="Últimos 30 días" tono={(datos.ContactosDirectos30d || 0) === 0 ? 'rojo' : 'normal'} />
                    <Tarjeta etiqueta="Promesas de pago" valor={datos.Promesas ?? 0} detalle={`${datos.Promesas90d ?? 0} en 90 días`} />
                    <Tarjeta etiqueta="Último pago" valor={datos.UltimoPagoFecha ? fmtUsd(datos.UltimoPagoMonto) : '-'} detalle={fmtDia(datos.UltimoPagoFecha)} tono="verde" />
                    <Tarjeta etiqueta="Pagado (90 días)" valor={fmtUsd(datos.MontoPagado90d)} detalle={`${plural(datos.Pagos90d ?? 0, 'pago registrado', 'pagos registrados')}`} tono="verde" />
                    <Tarjeta etiqueta="Mejor hora" valor={tieneHora ? hora(datos.HoraFrecuenteContacto) : '-'} detalle={tieneHora ? 'Más contactos directos' : 'Sin datos'} tono="indigo" />
                    <Tarjeta etiqueta="Canal efectivo" valor={datos.CanalMasEfectivo || '-'} detalle={datos.CanalMasEfectivo ? `${datos.CanalMasEfectivoTasa} % contacto directo` : 'Sin datos'} tono="indigo" pequeno />
                </div>
            </div>

            <LineaTiempo eventos={datos.LineaTiempo} />
        </section>
    );
}
